import React, { useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { buildPhotoUrl, buildHighResPhotoUrl } from "@/lib/api";

// Realtor list avatar.
//
// The face-detect background job (`/realtors/check-profile-photos`) fills
// in `photo_meta.is_placeholder=true` when the image URL returns a tiny
// broken/placeholder graphic (e.g. Realtor.ca "1x1" fallback, a Facebook
// icon SVG placeholder, brokerage logo, etc.).
//
// Rule requested by the operator: rather than showing a broken/logo image
// with a badge, just render the realtor's initials. "No photo" is always
// better than "wrong photo" for identifying someone at an open house.
//
// A lightbox is only opened when the current photo is real (not flagged
// as a placeholder). Clicking the initials fallback is a no-op so the
// parent row's own click handler still runs.
export default function PhotoAvatar({ realtor, size = "md" }) {
  const [zoomed, setZoomed] = useState(false);
  // Lightbox src starts on the highres variant; on load failure we fall back
  // to the standard `url` so we never end up with a broken image icon.
  const [lightboxSrcHiRes, setLightboxSrcHiRes] = useState(true);
  const initials =
    `${realtor.first_name?.[0] || ""}${realtor.last_name?.[0] || ""}`.toUpperCase() ||
    "??";
  const url = buildPhotoUrl(realtor);
  const hiResUrl = buildHighResPhotoUrl(realtor);
  const cls = size === "sm" ? "h-9 w-9" : "h-12 w-12";
  const meta = realtor.photo_meta || {};
  const hasPhotoUrl = !!(realtor.photo_url || url);
  const isPlaceholder =
    hasPhotoUrl && meta.checked_at && meta.is_placeholder === true;
  // Suppress the photo entirely for known-broken/logo entries and let the
  // AvatarFallback (initials) show through. See file header for rationale.
  const showPhoto = hasPhotoUrl && !isPlaceholder && !!url;
  const canZoom = showPhoto;
  const tooltip = canZoom ? "Click to enlarge photo" : "";
  const displayName = realtor.name || `${realtor.first_name || ""} ${realtor.last_name || ""}`.trim() || "Realtor";
  const handleClick = (e) => {
    if (!canZoom) return;
    // Stop the row-click / dialog-open handler from firing when the
    // user really only wanted to see the photo bigger.
    e.stopPropagation();
    e.preventDefault();
    setLightboxSrcHiRes(true);  // retry highres on each new open
    setZoomed(true);
  };
  return (
    <>
      <div
        className={`relative inline-block ${canZoom ? "cursor-zoom-in" : ""}`}
        title={tooltip}
        onClick={handleClick}
        data-testid={canZoom ? `photo-zoom-${realtor.id}` : undefined}
      >
        <Avatar className={cls}>
          {showPhoto ? <AvatarImage src={url} alt={realtor.name} /> : null}
          <AvatarFallback className="bg-slate-100 text-slate-700 text-xs font-medium">
            {initials}
          </AvatarFallback>
        </Avatar>
      </div>
      {canZoom ? (
        <Dialog open={zoomed} onOpenChange={setZoomed}>
          <DialogContent
            className="max-w-md p-0 bg-transparent border-0 shadow-none"
            data-testid={`photo-lightbox-${realtor.id}`}
          >
            <DialogTitle className="sr-only">{displayName} — profile photo</DialogTitle>
            <div className="rounded-lg overflow-hidden bg-slate-900 shadow-2xl">
              <img
                src={lightboxSrcHiRes && hiResUrl ? hiResUrl : url}
                alt={displayName}
                className="w-full h-auto max-h-[80vh] object-contain bg-slate-900"
                data-testid={`photo-lightbox-img-${realtor.id}`}
                onError={() => {
                  // Highres 404 (some Realtor.ca profiles don't ship one)
                  // — silently fall back to the standard-res URL.
                  if (lightboxSrcHiRes) setLightboxSrcHiRes(false);
                }}
              />
              <div className="px-4 py-2.5 text-sm text-white bg-slate-900/95 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium truncate">{displayName}</div>
                  {realtor.brokerage_name ? (
                    <div className="text-xs text-slate-300 truncate">{realtor.brokerage_name}</div>
                  ) : null}
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
}
